import React from 'react';
import { fpBackButton, fpCard, fpHeader, fpHeaderTitle, fpSticker } from '../utils/fpUi';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Crown, Sparkles, Star, Lock, Zap, Heart, Gift, Users, Smartphone, ShoppingBag } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useLanguage } from '../contexts/LanguageContext';

// Patreon tier data based on user's image
const tiers = [
  {
    id: 'bronze',
    name: 'Bronze Foot Lover',
    price: '$3',
    color: '#CD7F32',
    gradient: 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)',
    icon: Heart,
    benefits: [
      { key: 'earlyAccess', icon: Zap },
      { key: 'exclusivePreviews', icon: Star },
      { key: 'censoredVersions', icon: Lock },
      { key: 'pollAccess', icon: Users },
    ],
    highlight: 'previousPosts',
  },
  {
    id: 'gold',
    name: 'Gold Foot Uncensored',
    price: '$15',
    color: '#FFD700',
    gradient: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
    icon: Crown,
    popular: false,
    benefits: [
      { key: 'fullUncensored', icon: Sparkles },
      { key: 'altVersions', icon: Gift },
      { key: 'highRes', icon: Star },
      { key: 'extraChars', icon: Users },
      { key: 'monthlyPolls', icon: Zap },
    ],
    highlight: 'allUncensored',
    warning: 'realFootParadise',
  },
  {
    id: 'master',
    name: 'Master VIP',
    price: '$25',
    color: '#9B59B6',
    gradient: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)',
    icon: Crown,
    popular: true,
    limited: true,
    benefits: [
      { key: 'exclusiveMonthly', icon: Gift },
      { key: 'priorityVoting', icon: Zap },
      { key: 'directSupport', icon: Heart },
      { key: 'fullAppAccess', icon: Smartphone },
      { key: 'allShopComics', icon: ShoppingBag },
    ],
    highlight: 'allVIP',
  },
];

export default function News() {
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const { t, language } = useLanguage();

  const handleOpenPatreon = async () => {
    const url = 'https://www.patreon.com/foot_paradise';
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Translations for tier benefits
  const benefitTexts = {
    en: {
      earlyAccess: 'Early access to new posts',
      exclusivePreviews: 'Exclusive previews',
      censoredVersions: 'Censored versions before release',
      pollAccess: 'Access to polls for future content',
      previousPosts: 'Instant access to all previous posts',
      fullUncensored: 'Full uncensored illustrations',
      altVersions: 'Alternative poses & versions',
      highRes: 'High-resolution downloads',
      extraChars: 'Extra characters',
      monthlyPolls: 'Monthly character polls',
      allUncensored: 'Instant access to ALL uncensored content',
      realFootParadise: 'If you\'re here for the real Foot Paradise, this is the tier',
      exclusiveMonthly: 'Exclusive monthly comic pages',
      specialVersions: 'Special alternative versions',
      priorityVoting: 'Priority voting',
      directSupport: 'Your support directly helps create more comics',
      fullAppAccess: 'Full access to Android app (fully unlocked)',
      allShopComics: 'Access to all comics from the shop',
      allVIP: 'Instant access to all VIP content',
      limitedSpots: 'Limited spots available',
      mostPopular: 'MOST POPULAR',
    },
    es: {
      earlyAccess: 'Acceso anticipado a nuevos posts',
      exclusivePreviews: 'Previews exclusivos',
      censoredVersions: 'Versiones censuradas antes del lanzamiento',
      pollAccess: 'Acceso a encuestas para contenido futuro',
      previousPosts: 'Acceso instantáneo a todos los posts anteriores',
      fullUncensored: 'Ilustraciones completas sin censura',
      altVersions: 'Poses y versiones alternativas',
      highRes: 'Descargas en alta resolución',
      extraChars: 'Personajes extra',
      monthlyPolls: 'Encuestas mensuales de personajes',
      allUncensored: 'Acceso instantáneo a TODO el contenido sin censura',
      realFootParadise: 'Si buscas el verdadero Foot Paradise, este es el tier',
      exclusiveMonthly: 'Páginas de cómic mensuales exclusivas',
      specialVersions: 'Versiones alternativas especiales',
      priorityVoting: 'Voto prioritario',
      directSupport: 'Tu apoyo ayuda directamente a crear más cómics',
      fullAppAccess: 'Acceso completo a la app Android (totalmente desbloqueada)',
      allShopComics: 'Acceso a todos los cómics de la tienda',
      allVIP: 'Acceso instantáneo a todo el contenido VIP',
      limitedSpots: 'Lugares limitados disponibles',
      mostPopular: 'MÁS POPULAR',
    }
  };

  const getText = (key) => benefitTexts[language]?.[key] || benefitTexts.en[key];

  return (
    <div style={{
      ...styles.container,
      ...(isDarkMode ? styles.containerDark : {}),
    }}>
      <div style={{
        ...styles.header,
        ...(isDarkMode ? styles.headerDark : {}),
      }}>
        <button onClick={() => navigate('/')} style={{
          ...styles.backButton,
          ...(isDarkMode ? styles.backButtonDark : {}),
        }}>
          <ArrowLeft size={20} color={isDarkMode ? "#000" : "#fff"} />
        </button>
        <h2 style={styles.title}>Patreon</h2>
        <div style={styles.spacer} />
      </div>
      
      <div style={styles.content}>
        {/* Hero Section */}
        <div style={styles.heroSection}>
          <div style={styles.heroGlow}></div>
          <h1 style={styles.heroTitle}>{t('visitPatreon')}</h1>
          <p style={styles.heroSubtitle}>{t('patreonDescription')}</p>
        </div>

        {/* Tiers Section */}
        <div style={styles.tiersContainer}>
          {tiers.map((tier) => {
            const TierIcon = tier.icon;
            return (
              <div 
                key={tier.id} 
                style={{
                  ...styles.tierCard,
                  ...(tier.popular ? styles.tierCardPopular : {}),
                  borderColor: tier.color,
                }}
              >
                {tier.popular && (
                  <div style={{...styles.popularBadge, background: tier.gradient}}>
                    <span style={styles.popularText}>{getText('mostPopular')}</span>
                  </div>
                )}
                
                {/* Tier Header */}
                <div style={{...styles.tierHeader, background: tier.gradient}}>
                  <TierIcon size={32} color="#fff" />
                  <h3 style={styles.tierName}>{tier.name}</h3>
                  <div style={styles.tierPrice}>
                    <span style={styles.priceAmount}>{tier.price}</span>
                    <span style={styles.priceInterval}>/month</span>
                  </div>
                </div>

                {/* Benefits */}
                <div style={styles.benefitsList}>
                  {tier.benefits.map((benefit, idx) => {
                    const BenefitIcon = benefit.icon;
                    return (
                      <div key={idx} style={styles.benefitItem}>
                        <BenefitIcon size={16} color={tier.color} />
                        <span style={styles.benefitText}>{getText(benefit.key)}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Highlight */}
                <div style={{...styles.highlightBox, borderColor: tier.color, backgroundColor: `${tier.color}15`}}>
                  <Lock size={14} color={tier.color} />
                  <span style={{...styles.highlightText, color: tier.color}}>{getText(tier.highlight)}</span>
                </div>

                {/* Warning for Gold tier */}
                {tier.warning && (
                  <div style={styles.warningBox}>
                    <span style={styles.warningText}>⚠️ {getText(tier.warning)}</span>
                  </div>
                )}

                {/* Limited spots for Master */}
                {tier.limited && (
                  <div style={styles.limitedBox}>
                    <span style={styles.limitedText}>⚠️ {getText('limitedSpots')}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA Button */}
        <button onClick={handleOpenPatreon} style={styles.ctaButton}>
          <ExternalLink size={22} color="#fff" />
          <span style={styles.ctaText}>{t('openPatreon')}</span>
        </button>

        {/* Footer Note */}
        <p style={styles.footerNote}>{t('patreonNote')}</p>
      </div>

      {/* Shimmer animation style */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.02); opacity: 0.9; }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.3); }
          50% { box-shadow: 0 0 40px rgba(255, 215, 0, 0.6); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundImage: 'url(/wallpaper.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    display: 'flex',
    flexDirection: 'column',
  },
  containerDark: {
    backgroundImage: 'none',
    backgroundColor: '#000000',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    backgroundColor: '#1a1a2e',
    color: 'white',
    flexShrink: 0,
    ...fpHeader,
  },
  headerDark: {
    backgroundColor: '#000000',
    borderBottom: '1px solid #333',
  },
  backButton: {
    background: 'linear-gradient(135deg, #FF1493 0%, #FF69B4 100%)',
    border: 'none',
    cursor: 'pointer',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    boxShadow: '0 2px 8px rgba(255, 20, 147, 0.4)',
    ...fpBackButton,
  },
  backButtonDark: {
    background: '#ffffff',
    boxShadow: '0 2px 8px rgba(255, 255, 255, 0.2)',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
    ...fpHeaderTitle,
  },
  spacer: {
    width: '44px',
  },
  content: {
    flex: 1,
    padding: '20px 16px 40px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  // Hero Section
  heroSection: {
    textAlign: 'center',
    marginBottom: '24px',
    position: 'relative',
  },
  heroGlow: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '200px',
    height: '200px',
    background: 'radial-gradient(circle, rgba(255, 215, 0, 0.2) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#FFD700',
    margin: '0 0 12px 0',
    textShadow: '0 0 20px rgba(255, 215, 0, 0.5), 0 2px 4px rgba(0,0,0,0.5)',
    position: 'relative',
  },
  heroSubtitle: {
    fontSize: '15px',
    color: '#fff',
    margin: 0,
    opacity: 0.9,
    lineHeight: 1.5,
    maxWidth: '400px',
    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  },
  // Tiers Container
  tiersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: '100%',
    maxWidth: '400px',
    marginBottom: '24px',
  },
  tierCard: {
    backgroundColor: 'rgba(20, 20, 30, 0.95)',
    borderRadius: '16px',
    overflow: 'hidden',
    border: '2px solid',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
    position: 'relative',
    ...fpCard,
  },
  tierCardPopular: {
    transform: 'scale(1.02)',
    boxShadow: '0 12px 32px rgba(155, 89, 182, 0.4), 0 0 20px rgba(155, 89, 182, 0.2)',
  },
  popularBadge: {
    position: 'absolute',
    top: '-2px',
    right: '16px',
    padding: '6px 12px',
    borderRadius: '0 0 8px 8px',
    zIndex: 2,
  },
  popularText: {
    color: '#fff',
    fontSize: '10px',
    fontWeight: 'bold',
    letterSpacing: '1px',
  },
  tierHeader: {
    padding: '20px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  tierName: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#fff',
    margin: 0,
    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  },
  tierPrice: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
  },
  priceAmount: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#fff',
  },
  priceInterval: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.8)',
  },
  // Benefits
  benefitsList: {
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  benefitText: {
    fontSize: '13px',
    color: '#ccc',
    lineHeight: 1.4,
    flex: 1,
  },
  // Highlight Box
  highlightBox: {
    margin: '0 16px 16px',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  highlightText: {
    fontSize: '12px',
    fontWeight: '600',
    flex: 1,
  },
  // Warning Box
  warningBox: {
    margin: '0 16px 16px',
    padding: '10px 12px',
    backgroundColor: 'rgba(255, 193, 7, 0.15)',
    borderRadius: '8px',
    border: '1px solid rgba(255, 193, 7, 0.3)',
  },
  warningText: {
    fontSize: '11px',
    color: '#FFC107',
    fontStyle: 'italic',
  },
  // Limited Box
  limitedBox: {
    margin: '0 16px 16px',
    padding: '10px 12px',
    backgroundColor: 'rgba(255, 193, 7, 0.15)',
    borderRadius: '8px',
    border: '1px solid rgba(255, 193, 7, 0.3)',
  },
  limitedText: {
    fontSize: '11px',
    color: '#FFC107',
    fontWeight: '600',
  },
  // CTA Button
  ctaButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '18px 40px',
    background: 'linear-gradient(135deg, #FF1493 0%, #FF69B4 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '50px',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(255, 20, 147, 0.5)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    marginBottom: '20px',
    animation: 'pulse 2s infinite',
    ...fpSticker,
  },
  ctaText: {
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  // Footer
  footerNote: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)',
    textAlign: 'center',
    fontStyle: 'italic',
    maxWidth: '300px',
  },
};
